import type { BillingPeriod, DrawerMode } from '@monorepo/shared/schemas/common'
import {
  BILLING_PERIOD,
  BILLING_PERIOD_LABELS,
  BILLING_PERIODS,
  CHARGE_TYPE,
  DRAWER_MODE,
} from '@monorepo/shared/schemas/common'
import type { PlanCharge } from '@monorepo/shared/schemas/plans'
import { Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { toast } from '@/shared/lib/toast'
import { useAddCharge, useUpdateCharge } from '../../hooks/useAddons'
import type {
  CreatePlanCharge,
  UpdatePlanCharge,
} from '../../services/addonService'

interface RecurringChargeFormData {
  charge_name: string
  charge_code: string
  amount: number | null
  billing_frequency: BillingPeriod | null
  description: string
}

interface RecurringChargeDrawerProps {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  editingCharge?: PlanCharge
  mode?: DrawerMode
}

export function RecurringChargeDrawer({
  planId,
  open,
  onOpenChange,
  editingCharge,
  mode = DRAWER_MODE.create,
}: RecurringChargeDrawerProps) {
  const addCharge = useAddCharge()
  const updateCharge = useUpdateCharge()

  const [formData, setFormData] = useState<RecurringChargeFormData>({
    charge_name: '',
    charge_code: '',
    amount: null,
    billing_frequency: BILLING_PERIOD.monthly,
    description: '',
  })

  // Initialize form when editing
  useEffect(() => {
    if (mode === DRAWER_MODE.edit && editingCharge) {
      setFormData({
        charge_name: editingCharge.charge_name || '',
        charge_code: editingCharge.charge_code || '',
        amount: editingCharge.amount ?? null,
        billing_frequency:
          editingCharge.billing_frequency || BILLING_PERIOD.monthly,
        description: editingCharge.description || '',
      })
    } else {
      setFormData({
        charge_name: '',
        charge_code: '',
        amount: null,
        billing_frequency: BILLING_PERIOD.monthly,
        description: '',
      })
    }
  }, [mode, editingCharge])

  const handleSave = async () => {
    // Validation
    if (!formData.charge_name.trim()) {
      toast.error('Charge name is required')
      return
    }
    if (!formData.charge_code.trim()) {
      toast.error('Charge code is required')
      return
    }
    if (formData.amount === null || formData.amount < 0) {
      toast.error('Amount must be a positive number')
      return
    }
    if (!formData.billing_frequency) {
      toast.error('Billing frequency is required')
      return
    }

    try {
      const apiData: CreatePlanCharge | UpdatePlanCharge = {
        charge_name: formData.charge_name.trim(),
        charge_code: formData.charge_code.trim(),
        charge_type: CHARGE_TYPE.recurring,
        billing_frequency: formData.billing_frequency,
        amount: formData.amount,
        description: formData.description.trim() || null,
      }

      if (mode === DRAWER_MODE.create) {
        await addCharge.mutateAsync({
          planId,
          data: apiData as CreatePlanCharge,
        })

        toast.success('Recurring charge added successfully')
      } else {
        if (!editingCharge) return

        await updateCharge.mutateAsync({
          planId,
          chargeId: editingCharge.id,
          data: apiData as UpdatePlanCharge,
        })

        toast.success('Recurring charge updated successfully')
      }

      onOpenChange(false)
    } catch {
      // Error toast is handled by the mutation hook
    }
  }

  const isSaving = addCharge.isPending || updateCharge.isPending

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[500px] max-w-[700px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {mode === DRAWER_MODE.create
              ? 'Add Recurring Charge'
              : 'Edit Recurring Charge'}
          </DrawerTitle>
          <DrawerDescription>
            Configure a recurring charge that is billed on a regular schedule
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='space-y-6'>
            {/* Charge Name */}
            <div>
              <Label htmlFor='charge-name'>
                Charge Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='charge-name'
                type='text'
                placeholder='e.g., Platform Fee'
                value={formData.charge_name}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    charge_name: e.target.value,
                  }))
                }
                className='mt-1'
              />
            </div>

            {/* Charge Code */}
            <div>
              <Label htmlFor='charge-code'>
                Charge Code <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='charge-code'
                type='text'
                placeholder='e.g., platform_fee'
                value={formData.charge_code}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    charge_code: e.target.value,
                  }))
                }
                className='mt-1'
              />
              <p className='text-xs text-muted-foreground mt-1'>
                A unique identifier for this charge (lowercase, underscores
                allowed)
              </p>
            </div>

            {/* Amount */}
            <div>
              <Label htmlFor='amount'>
                Amount ($) <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='amount'
                type='number'
                step='0.01'
                min='0'
                placeholder='0.00'
                value={formData.amount ?? ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    amount: e.target.value ? parseFloat(e.target.value) : null,
                  }))
                }
                className='mt-1'
              />
            </div>

            {/* Billing Frequency */}
            <div>
              <Label htmlFor='billing-frequency'>
                Billing Frequency <span className='text-destructive'>*</span>
              </Label>
              <Select
                value={formData.billing_frequency || undefined}
                onValueChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    billing_frequency: value as BillingPeriod,
                  }))
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder='Select billing frequency' />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_PERIODS.map(period => (
                    <SelectItem key={period} value={period}>
                      {BILLING_PERIOD_LABELS[period]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                placeholder='Optional description of this charge'
                value={formData.description}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
                className='mt-1'
              />
            </div>
          </div>
        </div>

        <DrawerFooter className='border-t'>
          <div className='flex items-center justify-end gap-2'>
            <DrawerClose asChild>
              <Button variant='outline' disabled={isSaving}>
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              {mode === DRAWER_MODE.create ? 'Add Charge' : 'Save Changes'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
